import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import * as swcHelpers from '@swc/helpers';
import { actions as actions$1, uninitState, successState, failedState, loadingState, statusUninit, statusLoading, statusFailed, ApiConfigSelectors, ReducersRegistry, SagaRegistry } from '@grandvisionhq/state';
import { all, takeLatest, takeLeading, call, select, put, cancel } from 'redux-saga/effects';
import { addToCartSdAction, removeLineItemsSdAction } from '@grandvisionhq/analytics';
import { Carts } from '@grandvisionhq/api-client';
import { Util, toast } from '@grandvisionhq/common';
import { createSelector } from 'reselect';

/**
 * Calculates the total amount of items in the cart,
 * taking into account that 1 line item can have a quantity higher than 1
 * Does not add additional cart costs to the amount
 */ const calcCartItemsCount = (lineItems1, prescriptionGlassesLineItems = [])=>{
    const lineItemCount = lineItems1.filter((lineItem)=>!CommerceToolsApi.isAdditionalCartCosts(lineItem.productType && lineItem.productType.obj && lineItem.productType.obj.name.toString() || '')
    ).map((lineItems)=>lineItems.quantity || 0
    ).reduce((a, b)=>a + b
    , 0);
    const prescriptionGlassesLineItemCount = prescriptionGlassesLineItems.map((lineItem)=>lineItem.quantity || 0
    ).reduce((a, b)=>a + b
    , 0);
    return lineItemCount + prescriptionGlassesLineItemCount;
};

const CartService = ()=>{
    const localStorage = window.localStorage;
    const CART_TOKEN = 'stored_cart';
    const emptyCart = ()=>({
            cartVersion: 0,
            cartCount: 0
        })
    ;
    const setCartId = (cartId)=>setCartToStorage(swcHelpers.objectSpread({}, getCartFromStorage(), {
            cartId
        }))
    ;
    const getCartId = ()=>{
        const cart = getCartFromStorage();
        if (!cart) {
            return undefined;
        }
        return cart.cartId;
    };
    const setCartVersion = (cartVersion)=>setCartToStorage(swcHelpers.objectSpread({}, getCartFromStorage(), {
            cartVersion
        }))
    ;
    const getCartCount = ()=>{
        const cart = getCartFromStorage();
        if (!cart) {
            return 0;
        }
        return cart.cartCount;
    };
    const setCartCount = (cartCount)=>setCartToStorage(swcHelpers.objectSpread({}, getCartFromStorage(), {
            cartCount
        }))
    ;
    const setCartToStorage = (cart)=>{
        const newCart = cart || emptyCart();
        localStorage.setItem(CART_TOKEN, JSON.stringify(newCart));
    };
    const getCartVersion = ()=>{
        const cart = getCartFromStorage();
        if (!cart) {
            return 0;
        }
        return cart.cartVersion;
    };
    const getCartFromStorage = ()=>{
        const fromStorage = localStorage.getItem(CART_TOKEN);
        if (!fromStorage) {
            return emptyCart();
        }
        try {
            return JSON.parse(fromStorage);
        } catch (err) {
            console.error(`Error while parsing cart from localStorage; tried to parse "${fromStorage}`, err);
        }
        return emptyCart();
    };
    const removeCartFromStorage = ()=>{
        if (!localStorage.getItem(CART_TOKEN)) {
            return;
        }
        localStorage.removeItem(CART_TOKEN);
    };
    return {
        getCartId,
        setCartId,
        getCartCount,
        setCartCount,
        getCartVersion,
        setCartVersion,
        getCartFromStorage,
        setCartToStorage,
        removeCartFromStorage
    };
};
const hasLocalCart = ()=>{
    if (typeof window === 'undefined' || !window.localStorage) {
        return false;
    }
    const storedCart = JSON.parse(window.localStorage.getItem('stored_cart') || '{}');
    return storedCart && !!storedCart.cartId && !!storedCart.cartVersion;
};

const getDefaultShippingMethod = (shippingMethods)=>{
    const defaultMethod = shippingMethods.find((method)=>method.isDefault
    );
    if (defaultMethod) {
        return defaultMethod;
    }
    console.warn('No default shipping method configured in Commercetools');
    const firstMethod = shippingMethods[0];
    if (firstMethod) {
        return firstMethod;
    }
    console.error('There are no shipping method configured in Commercetools');
    return undefined;
};

var ref, ref1;
const getProductTypeNames = (cart)=>{
    var ref2;
    return (ref1 = cart && ((ref2 = cart.lineItems) === null || ref2 === void 0 ? void 0 : ref2.map((lineItem)=>{
        var ref4, ref3;
        return (ref = lineItem === null || lineItem === void 0 ? void 0 : (ref4 = lineItem.productType) === null || ref4 === void 0 ? void 0 : (ref3 = ref4.obj) === null || ref3 === void 0 ? void 0 : ref3.name) !== null && ref !== void 0 ? ref : 'Unknown';
    }))) !== null && ref1 !== void 0 ? ref1 : [];
};

const isEmptyCart = (cart)=>{
    var ref, ref1;
    const count = cart.lineItems.length;
    var ref2;
    const customCount = (ref2 = (ref = cart.customLineItems) === null || ref === void 0 ? void 0 : ref.length) !== null && ref2 !== void 0 ? ref2 : 0;
    var ref3;
    const pgCount = (ref3 = (ref1 = cart.prescriptionGlassesLineItems) === null || ref1 === void 0 ? void 0 : ref1.length) !== null && ref3 !== void 0 ? ref3 : 0;
    return count + pgCount + customCount < 1;
};

var _quantity;
const setDefaultQuantity = (item)=>swcHelpers.objectSpread({}, item, {
        quantity: (_quantity = item.quantity) !== null && _quantity !== void 0 ? _quantity : 1
    })
;
function parseAddToCartPayload(payload, isUpdate = false) {
    const { lineItems =[] , prescriptionGlassesBundleLineItems , currency , country , locale  } = payload;
    const enrichedLineItems = [];
    for (const _ref of lineItems){
        var lineItem = _ref;
        const { sku , quantity =1 , customAttributes , custom  } = lineItem;
        let enrichedLineItem = {
            sku,
            quantity
        };
        // This basically means contact-lens or contact-lens-bundles
        const hasCustomData = !!custom || !!customAttributes;
        if (hasCustomData) {
            const _ref = custom || customAttributes || {}, { type ='contact-lens'  } = _ref, fields = swcHelpers.objectWithoutProperties(_ref, [
                "type"
            ]);
            const customObject = isUpdate ? {
                type: {
                    key: type
                },
                fields
            } : swcHelpers.objectSpread({
                type
            }, fields);
            enrichedLineItem = swcHelpers.objectSpread({}, enrichedLineItem, {
                custom: customObject
            });
        }
        enrichedLineItems.push(enrichedLineItem);
    }
    const enrichedPrescriptionGlassesBundleLineItems = prescriptionGlassesBundleLineItems === null || prescriptionGlassesBundleLineItems === void 0 ? void 0 : prescriptionGlassesBundleLineItems.map((bundle)=>swcHelpers.objectSpread({}, bundle, {
            bundleMainLineItem: setDefaultQuantity(bundle.bundleMainLineItem),
            bundleChildLineItems: bundle.bundleChildLineItems.map(setDefaultQuantity)
        })
    );
    return {
        lineItems: enrichedLineItems,
        prescriptionGlassesLineItems: enrichedPrescriptionGlassesBundleLineItems,
        currency,
        country,
        locale,
        shippingAddress: {
            country
        },
        custom: {
            type: {
                key: 'order-grandvision',
                typeId: 'type'
            }
        }
    };
}

const CART_LOAD = 'cart/CART_LOAD';
const CART_SUCCESS = 'cart/CART_SUCCESS';
const CART_FAILED = 'cart/CART_FAILED';
const CART_VALIDATION_FAILED = 'cart/CART_VALIDATION_FAILED';
const CREATE_CART = 'cart/CREATE_CART';
const UPDATE_LOCAL_CART = 'cart/UPDATE_LOCAL_CART';
const UPDATE_CART = 'cart/UPDATE_CART';
const UPDATE_SHIPPING_METHOD = 'cart/UPDATE_SHIPPING_METHOD';
const ADD_LINE_ITEMS = 'cart/ADD_LINE_ITEMS';
const REPLICATE_CART = 'cart/REPLICATE_CART';
const REFRESH_CART = 'cart/REFRESH_CART';
const REFRESH_CART_FAILED = 'cart/REFRESH_CART_FAILED';
const REFRESH_CART_SUCCESS = 'cart/REFRESH_CART_SUCCESS';
const loadCart = (payload)=>({
        type: CART_LOAD,
        payload
    })
;
const cartSuccess = (payload)=>({
        type: CART_SUCCESS,
        payload
    })
;
const cartFailed = (payload)=>({
        type: CART_FAILED,
        payload
    })
;
const cartValidationFailed = (payload)=>({
        type: CART_VALIDATION_FAILED,
        payload
    })
;
const createCart$1 = (payload)=>({
        type: CREATE_CART,
        payload
    })
;
const updateLocalCart = (payload)=>({
        type: UPDATE_LOCAL_CART,
        payload
    })
;
const updateCart$1 = (payload)=>({
        type: UPDATE_CART,
        payload
    })
;
const updateShippingMethod = (payload)=>({
        type: UPDATE_SHIPPING_METHOD,
        payload
    })
;
const addLineItems$1 = (payload)=>({
        type: ADD_LINE_ITEMS,
        payload
    })
;
const removeLineItems = actions$1.action('cart/REMOVE_LINE_ITEMS', actions$1.payload());
const replicateCart$1 = (payload)=>({
        type: REPLICATE_CART,
        payload
    })
;
const refreshCart$1 = ()=>({
        type: REFRESH_CART
    })
;
const refreshCartFailed = (error)=>({
        type: REFRESH_CART_FAILED,
        error
    })
;
const refreshCartSuccess = (payload)=>({
        type: REFRESH_CART_SUCCESS,
        payload
    })
;

var actions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CART_LOAD: CART_LOAD,
  CART_SUCCESS: CART_SUCCESS,
  CART_FAILED: CART_FAILED,
  CART_VALIDATION_FAILED: CART_VALIDATION_FAILED,
  CREATE_CART: CREATE_CART,
  UPDATE_LOCAL_CART: UPDATE_LOCAL_CART,
  UPDATE_CART: UPDATE_CART,
  UPDATE_SHIPPING_METHOD: UPDATE_SHIPPING_METHOD,
  ADD_LINE_ITEMS: ADD_LINE_ITEMS,
  REPLICATE_CART: REPLICATE_CART,
  REFRESH_CART: REFRESH_CART,
  REFRESH_CART_FAILED: REFRESH_CART_FAILED,
  REFRESH_CART_SUCCESS: REFRESH_CART_SUCCESS,
  loadCart: loadCart,
  cartSuccess: cartSuccess,
  cartFailed: cartFailed,
  cartValidationFailed: cartValidationFailed,
  createCart: createCart$1,
  updateLocalCart: updateLocalCart,
  updateCart: updateCart$1,
  updateShippingMethod: updateShippingMethod,
  addLineItems: addLineItems$1,
  removeLineItems: removeLineItems,
  replicateCart: replicateCart$1,
  refreshCart: refreshCart$1,
  refreshCartFailed: refreshCartFailed,
  refreshCartSuccess: refreshCartSuccess
});

const initialState = uninitState();
const CartReducer = (state = initialState, action)=>{
    switch(action.type){
        case CART_LOAD:
            return loadingState();
        case CART_FAILED:
            return failedState(action.payload.error);
        case REFRESH_CART_SUCCESS:
        case CART_SUCCESS:
            return successState(action.payload.result);
        case UPDATE_LOCAL_CART:
            return successState(action.payload.cart);
        default:
            return state;
    }
};

const getCart$1 = (state)=>state.cart
;
const getCartResult = createSelector(getCart$1, (cart)=>cart && !statusUninit(cart) && !statusLoading(cart) && !statusFailed(cart) && !!cart.result ? cart.result : undefined
);
// Checks if cart is valid for checkout
const isCartResultValid = createSelector(getCartResult, (cart)=>!!cart && !!cart.id && !!cart.version && !!cart.country && !!cart.totalPrice && !!cart.shippingInfo && !isEmptyCart(cart) && cart.cartState === 'Active'
);
// Returns an array of productType strings that are present in the current cart state
const getCartProductTypes = createSelector(getCartResult, (cart)=>getProductTypeNames(cart)
);
// Checks if the cart contains any lineItems
const isCartEmpty = createSelector(getCartResult, (cart)=>!cart || isEmptyCart(cart)
);

var selectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getCart: getCart$1,
  getCartResult: getCartResult,
  isCartResultValid: isCartResultValid,
  getCartProductTypes: getCartProductTypes,
  isCartEmpty: isCartEmpty
});

/**
 * Updates cart count and version in the local storage,
 * then emits a global custom event on window to update other components. (e.g. Header)
 */ let changeEvent;
const handleCartChange = (cartService, cart)=>{
    if (!changeEvent) {
        changeEvent = Util.createCustomEvent('cart.change');
    }
    const itemCount = calcCartItemsCount(cart.lineItems, cart.prescriptionGlassesLineItems);
    cartService.setCartCount(itemCount);
    cartService.setCartVersion(cart.version);
    cartService.setCartId(cart.id);
    window.dispatchEvent(changeEvent);
};
function* refreshCart() {
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to refresh cart.');
    }
    if (!cartId) {
        yield put(cartFailed({
            error: {
                message: 'Need a valid cartId to proceed'
            }
        }));
        return;
    }
    try {
        const result = yield call(Carts.getCart, apiConfig.cartsUrl, {
            cartId
        });
        if (result.cartState !== 'Active') {
            yield put(cartFailed({
                error: {
                    message: 'Need an Active cartState to proceed'
                }
            }));
            return;
        }
        // If the cart is Active, store it in localstorage
        yield call(handleCartChange, cartService, result);
        yield put(refreshCartSuccess({
            result
        }));
    } catch (error) {
        yield put(refreshCartFailed(new Error(`Failed to refresh the cart: ${error.message}`)));
    }
}
function* createCart(action) {
    const cartService = yield call(CartService);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to create cart.');
    }
    try {
        const { cartDraft , formatProductUrl  } = action.payload;
        const result = yield call(Carts.createCart, apiConfig.cartsUrl, {
            cartDraft
        });
        yield call(addToCartSdAction, [
            ...result.lineItems,
            ...result.prescriptionGlassesLineItems || []
        ], formatProductUrl);
        yield call(handleCartChange, cartService, result);
        yield put(cartSuccess({
            result
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
        return error;
    }
}
function* getCart() {
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to get a cart.');
    }
    if (!cartId) {
        yield put(cartFailed({
            error: {
                message: 'Need cartId to fetch a cart'
            }
        }));
        yield cancel();
    }
    try {
        const result = yield call(Carts.getCart, apiConfig.cartsUrl, {
            cartId
        });
        yield call(handleCartChange, cartService, result);
        yield put(cartSuccess({
            result
        }));
    } catch (error) {
        yield put(cartFailed(error));
    }
}
function* setShippingMethod(action) {
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to set shipping methods.');
    }
    if (!cartId) {
        yield put(cartFailed({
            error: {
                message: 'Need cartId to fetch a cart'
            }
        }));
    }
    const cart = yield select(getCartResult);
    if (!cart) {
        throw new Error(`Need to have a cart to update!`);
    }
    try {
        const methods = yield call(Carts.getShippingMethods, apiConfig.cartsUrl, {
            cartId
        });
        const selectedShippingMethod = methods.find((method)=>method.name === action.payload.shippingMethodKey
        );
        const defaultShippingMethod = getDefaultShippingMethod(methods);
        const shippingMethod = {
            action: 'setShippingMethod',
            shippingMethod: {
                typeId: 'shipping-method',
                id: selectedShippingMethod ? selectedShippingMethod.id : defaultShippingMethod.id
            }
        };
        const updateCartPayload = {
            cartId,
            cartVersion: cart.version,
            actions: [
                shippingMethod
            ]
        };
        const updatedCart = yield call(Carts.updateCart, apiConfig.cartsUrl, updateCartPayload);
        yield call(handleCartChange, cartService, updatedCart);
        yield put(updateLocalCart({
            cart: updatedCart
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
    }
}
function* addLineItems(action) {
    const { lineItems , prescriptionGlassesBundleLineItems , formatProductUrl  } = action.payload;
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    let cart = yield select(getCartResult);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    // If there is a cart ID in local storage but no cart in Redux, refresh to get the latest cart
    if (!cart && cartId) {
        yield call(refreshCart);
        cart = yield select(getCartResult);
    }
    const doesNotContainLineItems = !lineItems && !prescriptionGlassesBundleLineItems;
    if (!apiConfig || !cart || !cartId || doesNotContainLineItems) {
        throw new Error('Need API configuration, cartId and lineItems to add line item!');
    }
    try {
        const skus = [];
        const actions$1 = [];
        lineItems === null || lineItems === void 0 ? void 0 : lineItems.forEach((lineItemDraft)=>{
            skus.push(lineItemDraft.sku);
            actions$1.push(swcHelpers.objectSpread({}, lineItemDraft, {
                action: 'addLineItem'
            }));
        });
        if (prescriptionGlassesBundleLineItems) {
            prescriptionGlassesBundleLineItems.forEach((bundle)=>{
                actions$1.push({
                    action: 'addPrescriptionGlassesLineItem',
                    prescriptionGlassesLineItem: bundle
                });
            });
        }
        const updateCartPayload = {
            cartId,
            cartVersion: cart.version,
            actions: actions$1
        };
        const updatedCart = yield call(Carts.updateCart, apiConfig.cartsUrl, updateCartPayload);
        var _sku;
        const newLineItems = updatedCart.lineItems.filter((lineItem)=>skus.includes((_sku = lineItem.variant.sku) !== null && _sku !== void 0 ? _sku : '')
        );
        if (prescriptionGlassesBundleLineItems) {
            var ref, ref1;
            // We need to use the unique ID from the lineItem to check which items
            // in the updated cart were newly added for prescription glasses.
            const oldPgLineItemIds = new Set(cart === null || cart === void 0 ? void 0 : (ref = cart.prescriptionGlassesLineItems) === null || ref === void 0 ? void 0 : ref.map(({ id  })=>id
            ));
            const pgItems = ((ref1 = updatedCart.prescriptionGlassesLineItems) === null || ref1 === void 0 ? void 0 : ref1.filter((lineItem)=>!oldPgLineItemIds.has(lineItem.id)
            )) || [];
            newLineItems.push(...pgItems);
        }
        yield call(addToCartSdAction, newLineItems, formatProductUrl);
        yield call(handleCartChange, cartService, updatedCart);
        yield put(updateLocalCart({
            cart: updatedCart
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
        return error;
    }
}
function* onRemoveLineItems(action) {
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    const { lineItems , formatProductUrl  } = action.payload;
    let cart = yield select(getCartResult);
    // If there is a cart ID in local storage but no cart in Redux, refresh to get the latest cart (checkout edge case)
    if (!cart && cartId) {
        yield call(refreshCart);
        cart = yield select(getCartResult);
    }
    if (!cart) {
        throw new Error(`Need to have a cart to update!`);
    }
    if (!apiConfig || !cartId || !lineItems) {
        throw new Error('Need API configuration, lineItems and cartId to function to remove line item!');
    }
    try {
        const actions$1 = action.payload.lineItems.map((lineItem)=>({
                action: 'removeLineItem',
                lineItemId: lineItem.id
            })
        );
        const updateCartPayload = {
            cartId,
            cartVersion: cart.version,
            actions: actions$1
        };
        const updatedCart = yield call(Carts.updateCart, apiConfig.cartsUrl, updateCartPayload);
        yield call(removeLineItemsSdAction, action.payload.lineItems, formatProductUrl);
        yield call(handleCartChange, cartService, updatedCart);
        yield put(updateLocalCart({
            cart: updatedCart
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
    }
}
function* updateCart(action) {
    const cartService = yield call(CartService);
    const cartId = yield call(cartService.getCartId);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    const { actions: actions$1  } = action.payload;
    const cart = yield select(getCartResult);
    if (!cart) {
        throw new Error(`Need to have a cart to update!`);
    }
    if (!apiConfig || !actions$1 || !cartId) {
        throw new Error('Need API configuration, actions and cartId to function to update cart!');
    }
    try {
        const updateCartPayload = {
            cartId,
            cartVersion: cart.version,
            actions: actions$1
        };
        const updatedCart = yield call(Carts.updateCart, apiConfig.cartsUrl, updateCartPayload);
        yield call(handleCartChange, cartService, updatedCart);
        yield put(updateLocalCart({
            cart: updatedCart
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
    }
}
// Replicates cart from an orderId
function* replicateCartFromOrderId(orderId) {
    const cartService = yield call(CartService);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to replicate cart.');
    }
    if (!orderId) {
        throw new Error('Need orderId in order to replicate cart from order');
    }
    const cart = yield call(Carts.replicateCart, apiConfig.cartsUrl, {
        reference: {
            typeId: 'order',
            id: orderId
        }
    });
    yield call(handleCartChange, cartService, cart);
    yield put(updateLocalCart({
        cart
    }));
}
// Replicates cart from an order or cart reference
function* replicateCart(action) {
    const cartService = yield call(CartService);
    const apiConfig = yield select(ApiConfigSelectors.getApiConfig);
    if (!apiConfig) {
        throw new Error('Need API configuration to replicate cart.');
    }
    try {
        const { reference  } = action.payload;
        const cart = yield call(Carts.replicateCart, apiConfig.cartsUrl, {
            reference
        });
        yield call(handleCartChange, cartService, cart);
        yield put(updateLocalCart({
            cart
        }));
    } catch (error) {
        yield put(cartFailed({
            error
        }));
    }
}
function* onCartFailed(action) {
    const { error  } = action.payload;
    const cartService = yield call(CartService);
    switch(error.statusCode){
        case 400:
            {
                // Cart is not active
                const cartId = yield call(cartService.getCartId);
                if (!cartId) {
                    throw new Error('Unable to restore cart without a valid cart ID!');
                }
                yield put(replicateCart$1({
                    reference: {
                        typeId: 'cart',
                        id: cartId
                    }
                }));
                yield call(toast.failed, 'cart.errors.cartNotInActiveState');
                break;
            }
        case 404:
            {
                // Cart not found
                yield call(cartService.removeCartFromStorage);
                yield call(toast.failed, 'cart.errors.cartNotFound');
                break;
            }
        case 409:
            {
                // Cart version mismatch
                yield put(refreshCart$1());
                yield call(toast.failed, 'cart.errors.cartVersionMismatch');
                break;
            }
        case 422:
            {
                // Cart validation failed
                yield put(cartValidationFailed({
                    error
                }));
                break;
            }
        default:
            {
                yield call(cartService.removeCartFromStorage);
            }
    }
}
function* handleValidationError(action) {
    var ref;
    const { error  } = action.payload;
    /**
   * In some cases the cart service will return an ErrorCode based on failed validations.
   * In these cases we want to show a toast to the user with the corresponding label for that validation error code.
   */ const validationErrorLabelMap = {
        ContainsMutuallyExclusiveProductTypes: 'validations.containsMutuallyExclusiveProductTypes',
        MaxQuantitySkuExceeded: 'validations.maxQuantitySkuExceeded',
        MaxMasterVariantQuantityExceeded: 'validations.maxMasterVariantQuantityExceeded',
        MaxVariantsPerProductTypeQuantityExceeded: 'validations.maxVariantsPerProductTypeQuantityExceeded',
        QuantityExceededPrescriptionExpiryDate: 'validations.quantityExceededPrescriptionExpiryDate',
        UnavailableProductsInCart: 'validations.unavailableProductsInCart',
        PrescriptionGlassesInvalidBundleCombination: 'validations.prescriptionGlassesInvalidBundleCombination',
        PrescriptionGlassesInvalidOpticalParameters: 'validations.prescriptionGlassesInvalidOpticalParameters'
    };
    const errorCode = (ref = error.customData) === null || ref === void 0 ? void 0 : ref.validationErrorCode;
    if (errorCode && errorCode in validationErrorLabelMap) {
        yield call(toast.failed, `cart.${validationErrorLabelMap[errorCode]}`);
    } else {
        throw new Error('Unknown validation code returned from server');
    }
}
function* sagas() {
    yield all([
        takeLatest(CART_LOAD, getCart),
        takeLeading(CREATE_CART, createCart),
        takeLeading(UPDATE_SHIPPING_METHOD, setShippingMethod),
        takeLeading(ADD_LINE_ITEMS, addLineItems),
        takeLeading(removeLineItems.type, onRemoveLineItems),
        takeLeading(REPLICATE_CART, replicateCart),
        takeLeading(UPDATE_CART, updateCart),
        takeLatest(CART_FAILED, onCartFailed),
        takeLatest(CART_VALIDATION_FAILED, handleValidationError),
        takeLatest(REFRESH_CART, refreshCart), 
    ]);
}

ReducersRegistry.register('cart', CartReducer);
SagaRegistry.register('cart', sagas);

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Actions: actions,
  Selectors: selectors,
  handleCartChange: handleCartChange,
  getCart: getCart,
  refreshCart: refreshCart,
  replicateCartFromOrderId: replicateCartFromOrderId
});

const Utils = {
    calcCartItemsCount,
    CartService,
    hasLocalCart,
    parseAddToCartPayload
};

export { index as ReduxDataSource, Utils };
//# sourceMappingURL=index.js.map
